import React from 'react';
import { RefreshCw, Wifi, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { useKioskHealth } from '../../../hooks/useApi';
import { useFormatDate } from '../../../hooks/useFormatDate';

const KioskHealthTab: React.FC = () => {
  const fmt = useFormatDate();
  const { data, isLoading, error, refetch } = useKioskHealth();

  const kiosks = data?.data || [];
  const onlineCount = kiosks.filter(k => k.isOnline).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Wifi className="h-5 w-5" />
          Kiosk Health
          {kiosks.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({onlineCount}/{kiosks.length} online)
            </span>
          )}
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-destructive">Failed to load kiosk health data</p>
            <button onClick={() => refetch()} className="mt-2 text-sm text-primary hover:underline">
              Try again
            </button>
          </div>
        ) : kiosks.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No kiosk domains registered yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Uptime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kiosks.map((kiosk) => (
                  <TableRow key={kiosk.domainId} className="hover:bg-accent/50">
                    <TableCell>
                      {kiosk.isOnline ? (
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                          <span className="text-green-600 dark:text-green-400 text-sm font-medium">Online</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-red-500" />
                          <span className="text-destructive text-sm font-medium">Offline</span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{kiosk.domain}</TableCell>
                    <TableCell className="text-muted-foreground">{kiosk.label || '—'}</TableCell>
                    <TableCell className="font-mono text-sm">{kiosk.ipAddress || '—'}</TableCell>
                    <TableCell>
                      {kiosk.lastSeenAt ? (
                        <span className="text-sm">
                          {fmt(kiosk.lastSeenAt)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {kiosk.minutesSinceLastSeen !== null ? (
                        <span className={`flex items-center gap-1 text-sm ${
                          kiosk.isOnline ? 'text-green-600 dark:text-green-400' : 'text-destructive'
                        }`}>
                          <Clock className="h-3.5 w-3.5" />
                          {kiosk.minutesSinceLastSeen < 60
                            ? `${kiosk.minutesSinceLastSeen}m ago`
                            : `${Math.floor(kiosk.minutesSinceLastSeen / 60)}h ${kiosk.minutesSinceLastSeen % 60}m ago`}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">
          Auto-refreshes every 30 seconds. Kiosk pings every 30s via <code className="font-mono">POST /api/kiosk/heartbeat</code>.
          Offline alert fires after 5 minutes of inactivity.
        </p>
      </CardContent>
    </Card>
  );
};

export default KioskHealthTab;
